/**
 * AnimationClipPicker - Shared clip selection UI with built-in/custom tab switching.
 *
 * Handles:
 * - Tab switching between built-in (embedded) and custom (external) clips
 * - Embedded clips: polled from the target's Animation behavior
 * - Custom clips: loaded from an external `src` GLB via getClipNamesForSrc
 * - "No Animation behavior" placeholder with an Add button
 *
 * Also exports AnimationActionFields — a wrapper used by the flow editor action node
 * that adds src, mode, speed, blend fields around the clip picker.
 */

import React, { useState, useEffect, useCallback } from 'react';
import type { SceneObject } from 'trace-viewer';
import { Animation } from 'trace-viewer/behaviors';
import { getClipNamesForSrc } from 'trace-viewer/animation';
import { dispatchBehaviorChanged, EDITOR_EVENTS } from '../editor-events';

// ============================================================================
// Types
// ============================================================================

export type ClipMode = 'embedded' | 'custom';

export interface AnimationClipPickerProps {
    /** Currently selected clip name */
    clip: string | undefined;
    /** External animation source (GLB path) — used to load custom clip names */
    src?: string;
    /** Called when clip selection changes */
    onClipChange: (clip: string | undefined, mode: ClipMode) => void;
    /** Called when the user switches tabs (so consumer can show/hide src field, etc.) */
    onModeChange?: (mode: ClipMode) => void;
    /** The SceneObject whose Animation behavior provides embedded clip names.
     *  Also used to access the Scene and ResourceManager for loading src clips.
     *  If null/undefined, the picker shows a text input as fallback. */
    sceneObject?: SceneObject | null;
    /** Compact mode for flow editor nodes (smaller text) */
    compact?: boolean;
    /** Lock to a single mode, hiding the tab row. */
    restrictMode?: ClipMode;
}

// ============================================================================
// AnimationClipPicker
// ============================================================================

export const AnimationClipPicker: React.FC<AnimationClipPickerProps> = ({
    clip,
    src,
    onClipChange,
    onModeChange,
    sceneObject,
    compact = false,
    restrictMode,
}) => {
    const [embeddedClipNames, setEmbeddedClipNames] = useState<string[]>([]);
    const [srcClipNames, setSrcClipNames] = useState<string[]>([]);

    // Track the Animation behavior in state so we re-render when it's added/removed
    const [animBehavior, setAnimBehavior] = useState(
        () => sceneObject?.getBehavior(Animation) ?? null
    );

    // Re-check for Animation behavior when sceneObject changes or BEHAVIOR_CHANGED fires
    useEffect(() => {
        const check = () => setAnimBehavior(sceneObject?.getBehavior(Animation) ?? null);
        check();
        window.addEventListener(EDITOR_EVENTS.BEHAVIOR_CHANGED, check);
        return () => window.removeEventListener(EDITOR_EVENTS.BEHAVIOR_CHANGED, check);
    }, [sceneObject]);

    // Derive initial tab from whether src is set (or locked by restrictMode)
    const [clipMode, setClipMode] = useState<ClipMode>(
        () => restrictMode ?? ((src || (clip && animBehavior && !animBehavior.getEmbeddedClipNames().includes(clip)))
            ? 'custom'
            : 'embedded')
    );

    // Poll embedded clip names from the Animation behavior
    useEffect(() => {
        if (!animBehavior) return;

        const update = () => {
            setEmbeddedClipNames(animBehavior.getEmbeddedClipNames());
        };
        update();
        const intervalId = setInterval(update, 500);
        return () => clearInterval(intervalId);
    }, [animBehavior]);

    // Load clip names from external src GLB when src changes
    useEffect(() => {
        if (!src || !sceneObject) {
            setSrcClipNames([]);
            return;
        }

        let cancelled = false;
        void getClipNamesForSrc(src, sceneObject.babylonScene, sceneObject.resources)
            .then(names => {
                if (!cancelled) setSrcClipNames(names);
            });

        return () => { cancelled = true; };
    }, [src, sceneObject]);

    const handleClipChange = useCallback((value: string) => {
        if (!value && animBehavior) {
            animBehavior.stop();
        }
        onClipChange(value || undefined, clipMode);
    }, [clipMode, onClipChange, animBehavior]);

    const handleTabChange = useCallback((mode: ClipMode) => {
        setClipMode(mode);
        onModeChange?.(mode);
        // Clear clip when switching tabs so stale names don't carry over.
        // Skip if onModeChange is provided — consumer handles clip + src together.
        if (!onModeChange) {
            onClipChange(undefined, mode);
        }
    }, [onClipChange, onModeChange]);

    const clipList = clipMode === 'embedded' ? embeddedClipNames : srcClipNames;
    const hasClips = clipList.length > 0;

    const clipInList = !!clip && clipList.includes(clip);
    const selectValue = clipInList ? clip : '';

    const tabFn = compact ? compactTabStyle : fullTabStyle;
    const selectStyle = compact ? compactSelectStyle : fullSelectStyle;

    const emptyLabel = hasClips
        ? '(None)'
        : clipMode === 'custom' ? '(Set src below)' : '(No clips)';

    // No Animation behavior on target — show message + add button instead of tabs
    if (!animBehavior && sceneObject) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ color: 'white', opacity: 0.8, fontSize: compact ? '9px' : '11px' }}>
                    No Animation behavior
                </span>
                <button
                    onClick={() => {
                        void sceneObject.addBehavior(Animation).then(() => {
                            dispatchBehaviorChanged({
                                objectPath: sceneObject.getPath(),
                                behaviorType: 'Animation',
                                action: 'added',
                            });
                        });
                    }}
                    style={{
                        padding: compact ? '2px 6px' : '3px 8px',
                        fontSize: compact ? '9px' : '11px',
                        background: 'rgba(0,0,0,0.3)',
                        border: '1px solid rgba(255,255,255,0.4)',
                        borderRadius: '3px',
                        color: 'white',
                        cursor: 'pointer',
                    }}
                >
                    + Add
                </button>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {/* Tab row — hidden when restrictMode locks to one mode */}
            {!restrictMode && (
                <div style={{ display: 'flex', gap: compact ? '2px' : '4px' }}>
                    <button onClick={() => handleTabChange('embedded')} style={tabFn(clipMode === 'embedded')}>
                        Built-in{embeddedClipNames.length > 0 ? ` (${embeddedClipNames.length})` : ''}
                    </button>
                    <button onClick={() => handleTabChange('custom')} style={tabFn(clipMode === 'custom')}>
                        Custom{srcClipNames.length > 0 ? ` (${srcClipNames.length})` : ''}
                    </button>
                </div>
            )}

            {/* Clip selector */}
            <select
                value={selectValue}
                onChange={(e) => handleClipChange(e.target.value)}
                style={selectStyle}
            >
                <option value="">{emptyLabel}</option>
                {clipList.map(name => (
                    <option key={name} value={name}>{name}</option>
                ))}
            </select>
        </div>
    );
};

// ============================================================================
// Styles
// ============================================================================

const compactTabStyle = (active: boolean): React.CSSProperties => ({
    padding: '2px 6px',
    fontSize: '9px',
    border: 'none',
    borderBottom: active ? '2px solid rgba(255,255,255,0.8)' : '2px solid transparent',
    background: 'none',
    color: active ? 'white' : 'rgba(255,255,255,0.5)',
    cursor: 'pointer',
});

const fullTabStyle = (active: boolean): React.CSSProperties => ({
    padding: '3px 2px',
    marginRight: '8px',
    fontSize: '11px',
    border: 'none',
    borderBottom: active ? '2px solid #7ab' : '2px solid transparent',
    background: 'none',
    color: active ? '#ddd' : '#777',
    cursor: 'pointer',
});

const compactSelectStyle: React.CSSProperties = {
    padding: '3px 6px',
    background: 'rgba(0,0,0,0.3)',
    border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: '3px',
    color: 'white',
    fontSize: '10px',
    cursor: 'pointer',
};

const fullSelectStyle: React.CSSProperties = {
    padding: '4px 8px',
    backgroundColor: '#333',
    border: '1px solid #555',
    borderRadius: '3px',
    color: '#fff',
    fontSize: '12px',
    outline: 'none',
    cursor: 'pointer',
};

